import { useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/Reveal";
import { CALENDLY_URL } from "@/lib/scroll";

const DAYS = [
  {
    day: "1",
    title: "Kickoff & ICP sign-off",
    desc: "A 45-minute kickoff, and we lock your ideal customer profile together.",
  },
  {
    day: "2–3",
    title: "Account & decision-maker research",
    desc: "Deep research into your market: the 50 target accounts and the right people inside them.",
  },
  {
    day: "4",
    title: "Messaging & follow-up sequences",
    desc: "Three outreach angles written for email and LinkedIn, plus your 4-touch follow-up sequence.",
  },
  {
    day: "5",
    title: "Tracker setup & first outreach batch",
    desc: "Your pipeline tracker is set up, and the first outreach batch goes out.",
  },
  {
    day: "6",
    title: "Test calls, follow-ups, reply handling",
    desc: "Test calls made, follow-ups sent, and early replies handled on your behalf.",
  },
  {
    day: "7",
    title: "Handover call, results snapshot, 30-day plan",
    desc: "We walk through everything together: your one-page results snapshot and 30-day execution plan.",
  },
];

const Process = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start 0.75", "end 0.6"],
  });
  const scaleY = useSpring(scrollYProgress, { stiffness: 90, damping: 25 });

  return (
    <section
      id="process"
      ref={ref}
      data-testid="process-section"
      className="scroll-mt-24 py-20 lg:py-28"
    >
      <div className="mx-auto grid max-w-7xl gap-16 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr] lg:px-12">
        <div className="lg:sticky lg:top-28 lg:self-start">
          <Reveal>
            <Eyebrow>How it works</Eyebrow>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-serif text-3xl font-medium leading-[1.18] tracking-tight text-ink sm:text-4xl lg:text-5xl">
              Seven days, from blank page to live test.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-smoke sm:text-lg">
              A focused week with a clear job each day — you give 2–3 hours, the
              sprint does the rest.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="process-cta"
              className="mt-9 inline-flex items-center gap-2 rounded-full border border-ink bg-cream px-7 py-3.5 text-base font-medium text-ink transition-all duration-300 hover:bg-ink hover:text-cream"
            >
              Book a 15-minute fit call
              <ArrowRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>

        <div className="relative">
          <div className="absolute bottom-3 left-[27px] top-3 w-px bg-line" />
          <motion.div
            style={{ scaleY }}
            className="absolute bottom-3 left-[27px] top-3 w-px origin-top bg-terra"
          />
          <div className="space-y-5">
            {DAYS.map((d, i) => (
              <Reveal key={d.day} delay={i * 0.05}>
                <div
                  data-testid={`process-day-${d.day.replace("–", "-")}`}
                  className="group flex gap-6 rounded-2xl p-4 transition-colors duration-300 hover:bg-white"
                >
                  <div className="relative z-10 flex h-14 w-14 shrink-0 flex-col items-center justify-center rounded-full border border-line bg-cream font-mono leading-tight text-ink transition-colors duration-300 group-hover:border-terra group-hover:text-terra">
                    <span className="text-[9px] uppercase tracking-[0.18em] text-smokeLight">
                      Day
                    </span>
                    <span className="text-sm font-semibold">{d.day}</span>
                  </div>
                  <div className="pt-1">
                    <h3 className="font-serif text-xl text-ink">{d.title}</h3>
                    <p className="mt-1.5 text-sm leading-relaxed text-smoke">{d.desc}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Process;
